import { useCallback, useState } from 'react';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { CaseGrant, CaseRole, SFAccountCaseGrants } from '../models/CaseManagementModels';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';

export interface CaseGrantsHook {
  accounts: SFAccountCaseGrants[];
  loadCaseGrants: AsyncState<void, () => Promise<void>>;
  upsertCaseGrant: AsyncState<void, (crmAccountId: string, email: string, role: CaseRole) => Promise<void>>;
  revokeCaseGrant: AsyncState<void, (crmAccountId: string, email: string) => Promise<void>>;
}

// upsertCaseGrant/revokeCaseGrant patch `accounts` locally from the mutation response instead of
// re-running loadCaseGrants -- re-running it would flip loadCaseGrants back to Pending and, since
// AsyncSuspense swaps its whole subtree out for the loading fallback on Pending, the entire table
// would unmount and reappear on every edit instead of just updating the changed row.
const upsertGrantInAccounts = (
  accounts: SFAccountCaseGrants[],
  crmAccountId: string,
  grant: CaseGrant
): SFAccountCaseGrants[] =>
  accounts.map((account) => {
    if (account.crmAccountId !== crmAccountId) {
      return account;
    }
    const hasExistingGrant = account.grants.some((existing) => existing.email === grant.email);
    const grants = hasExistingGrant
      ? account.grants.map((existing) => (existing.email === grant.email ? grant : existing))
      : [...account.grants, grant];
    return { ...account, grants };
  });

const removeGrantFromAccounts = (
  accounts: SFAccountCaseGrants[],
  crmAccountId: string,
  email: string
): SFAccountCaseGrants[] =>
  accounts.map((account) =>
    account.crmAccountId === crmAccountId
      ? { ...account, grants: account.grants.filter((grant) => grant.email !== email) }
      : account
  );

export const useCaseGrants = (): CaseGrantsHook => {
  const [accounts, setAccounts] = useState<SFAccountCaseGrants[]>([]);
  const apiClient = useAuthenticatedApiContext();

  const loadCaseGrants = useAsync(
    useCallback(async (): Promise<void> => {
      const response = await apiClient.getCaseGrants();
      setAccounts(response.accounts);
    }, [apiClient]),
    { autoReset: true }
  );
  useAsyncEffect(loadCaseGrants, []);

  const upsertCaseGrant = useAsync(
    useCallback(
      async (crmAccountId: string, email: string, role: CaseRole): Promise<void> => {
        const grant = await apiClient.upsertCaseGrant({ crmAccountId, email, role });
        setAccounts((current) => upsertGrantInAccounts(current, crmAccountId, grant));
      },
      [apiClient]
    ),
    { autoReset: true }
  );

  const revokeCaseGrant = useAsync(
    useCallback(
      async (crmAccountId: string, email: string): Promise<void> => {
        await apiClient.revokeCaseGrant({ crmAccountId, email });
        setAccounts((current) => removeGrantFromAccounts(current, crmAccountId, email));
      },
      [apiClient]
    ),
    { autoReset: true }
  );

  return { accounts, loadCaseGrants, upsertCaseGrant, revokeCaseGrant };
};
